import { useCallback } from 'react';
import type { ReportSource } from '@terra/shared-contracts-reports';
import { createTabScopedStore } from './createTabScopedStore';

export const REPORT_SOURCE_CHIP = {
  ALL: 'ALL',
  MANUAL: 'MANUAL',
  SCHEDULED: 'SCHEDULED',
  ON_DEMAND: 'ON_DEMAND',
} as const satisfies Record<'ALL' | ReportSource, string>;

export type ReportSourceChip =
  (typeof REPORT_SOURCE_CHIP)[keyof typeof REPORT_SOURCE_CHIP];

export type ReportsTabFilterState = {
  searchTerm: string;
  sourceChip: ReportSourceChip;
  createdStartDate: Date | null;
  createdEndDate: Date | null;
};

const useReportsTabScopedFilterStoreBase =
  createTabScopedStore<ReportsTabFilterState>({
    searchTerm: '',
    sourceChip: REPORT_SOURCE_CHIP.ALL,
    createdStartDate: null,
    createdEndDate: null,
  });

export function useTabScopedReportsFilters() {
  const { filters, update, reset, clearTabScope } =
    useReportsTabScopedFilterStoreBase();

  const setSearchTerm = useCallback(
    (searchTerm: string) => update({ searchTerm }),
    [update],
  );
  const setSourceChip = useCallback(
    (sourceChip: ReportSourceChip) => update({ sourceChip }),
    [update],
  );
  const setCreatedStartDate = useCallback(
    (createdStartDate: Date | null) => update({ createdStartDate }),
    [update],
  );
  const setCreatedEndDate = useCallback(
    (createdEndDate: Date | null) => update({ createdEndDate }),
    [update],
  );
  const clearListFilters = useCallback(
    () => update({ createdStartDate: null, createdEndDate: null }),
    [update],
  );

  return {
    ...filters,
    setSearchTerm,
    setSourceChip,
    setCreatedStartDate,
    setCreatedEndDate,
    clearListFilters,
    resetFilters: reset,
    clearTabScope,
  };
}
